import './style.modules.scss'

import { MaterialIcon, Spacer, SpacerSpacing } from '@ampcore/adel-react'
import { AmpcoreMaterialIconId } from '@ampcore/ampcom-contracts'
import { AnalyticsTagBuilder } from '@dai-team/analytics-library'
import React, { useCallback, useMemo, useState } from 'react'

import { rteContentFilter } from '../../lib/helpers/rteContentFilter'
import { CarouselProps, CarouselSlide, CarouselVariant } from './Carousel.type'
import { resolveBackgroundClasses } from './carouselStyles'

/**
 * Carousel renders an optional intro block followed by a single-slide
 * carousel. One slide is visible at a time; previous/next arrows and clickable
 * pagination bullets move between slides.
 *
 * Accessibility:
 * - Arrow and pagination controls are real `<button>` elements (focusable,
 *   operable with Enter/Space natively).
 * - The active slide is exposed with `aria-current`; inactive slides are
 *   `aria-hidden` and removed from the tab order via `inert`-style tabIndex.
 * - A visually hidden `aria-live` region announces the current slide position.
 */
export const Carousel = (props: CarouselProps): JSX.Element | null => {
  const {
    title,
    introductionContent,
    slideList,
    backgroundColor,
    isFullBleed = false,
    componentSpacing,
    loop = true,
    leftArrowDataAnalytics,
    rightArrowDataAnalytics,
    variant = CarouselVariant.COMPLEX,
    testId
  } = props

  const isLegacy = variant === CarouselVariant.LEGACY

  const slides: CarouselSlide[] = useMemo(() => {
    if (!slideList) {
      return []
    }
    return Array.isArray(slideList) ? slideList : [slideList]
  }, [slideList])

  const [position, setPosition] = useState(0)
  const slideCount = slides.length

  const atb = useMemo(
    () => new AnalyticsTagBuilder().setDefaultName('CarouselComplex'),
    []
  )

  const goToSlide = useCallback(
    (index: number) => {
      if (index < 0) {
        setPosition(loop ? slideCount - 1 : 0)
        return
      }
      if (index > slideCount - 1) {
        setPosition(loop ? 0 : slideCount - 1)
        return
      }
      setPosition(index)
    },
    [slideCount, loop]
  )

  const moveLeft = useCallback(
    () => goToSlide(position - 1),
    [goToSlide, position]
  )
  const moveRight = useCallback(
    () => goToSlide(position + 1),
    [goToSlide, position]
  )

  if (slideCount === 0) {
    return null
  }

  // The legacy variant always renders on the brand background with reversed
  // contrast, ignoring any passed color.
  const resolved = isLegacy
    ? { bgClass: 'u-bgColorBrand', contrastClass: 'Content--reversed' }
    : resolveBackgroundClasses(backgroundColor)
  const { bgClass, contrastClass } = resolved

  // Full-bleed takes no default margin; otherwise default to full. `||` (not
  // `??`) so an empty-string spacing is treated as "not provided".
  const spacing =
    componentSpacing || (isFullBleed ? undefined : SpacerSpacing.FULL)

  const outerClasses = [
    'Carousel',
    isLegacy ? 'Carousel--legacy' : '',
    isFullBleed ? `${bgClass} ${contrastClass} u-paddingVert32` : ''
  ]
    .filter(Boolean)
    .join(' ')

  const innerColorClasses = isFullBleed
    ? ''
    : `${bgClass} ${contrastClass} u-paddingBottom48`

  const showControls = slideCount > 1

  return (
    <Spacer spacing={spacing}>
      <div className={outerClasses} data-testid={testId ? testId : null}>
        {introductionContent && (
          <div className="u-sizeConstrained">
            <div className={`u-sizeFull ${innerColorClasses}`}>
              <div
                className="Content content-block"
                dangerouslySetInnerHTML={{
                  __html: rteContentFilter(introductionContent)
                }}
                data-testid={testId ? `${testId}-introduction` : null}
              />
            </div>
          </div>
        )}

        <section
          className="u-posRelative u-marginCenter u-sizeConstrained"
          aria-label="Carousel"
          {...atb.dataAnalyticAttribute('CarouselComplex', 'carousel-complex')}
        >
          <div className={`u-lg-size12of12 ${innerColorClasses}`}>
            {title && (
              <h2
                className="u-paddingBottom40 u-paddingLeft24"
                dangerouslySetInnerHTML={{ __html: rteContentFilter(title) }}
                data-testid={testId ? `${testId}-title` : null}
              />
            )}

            <div className={isLegacy ? 'u-paddingHoriz80' : 'u-paddingHoriz24'}>
              <div className="Carousel-track u-posRelative u-overflowHidden u-sizeFull">
                <ul
                  className="Carousel-trackList u-flex u-posRelative u-sizeFull"
                  style={{ transform: `translateX(-${position * 100}%)` }}
                  data-testid={testId ? `${testId}-track` : null}
                >
                  {slides.map((slide, index) => (
                    <li
                      key={index}
                      className="Carousel-slide u-posRelative u-flexShrink0 u-sizeFull Grid u-sm-flexCol u-flexAlignItemsCenter"
                      aria-hidden={index !== position}
                      aria-current={index === position}
                      aria-label={`Slide ${index + 1} of ${slideCount}`}
                    >
                      <CarouselSlideContent
                        slide={slide}
                        isActive={index === position}
                        variant={variant}
                      />
                    </li>
                  ))}
                </ul>
              </div>

              {showControls && (
                <div
                  className="Carousel-pagination u-paddingTop24 u-textCenter"
                  data-testid={testId ? `${testId}-pagination` : null}
                >
                  {slides.map((_, index) => (
                    <button
                      key={index}
                      type="button"
                      className={`Carousel-bullet u-inlineBlock u-posRelative${
                        index === position ? ' is-active' : ''
                      }`}
                      aria-label={`Go to slide ${index + 1} of ${slideCount}`}
                      aria-current={index === position}
                      onClick={() => goToSlide(index)}
                      data-testid={testId ? `${testId}-bullet-${index}` : null}
                    />
                  ))}
                </div>
              )}

              {showControls && (
                <>
                  <button
                    type="button"
                    className="Carousel-arrow Carousel-arrowPrev u-posAbsolute u-marginLeft32"
                    aria-label="Slide Left"
                    onClick={moveLeft}
                    disabled={!loop && position === 0}
                    data-testid={testId ? `${testId}-slide-left` : null}
                    {...atb.dataAnalyticAttribute(
                      leftArrowDataAnalytics,
                      'slide-left'
                    )}
                  >
                    <MaterialIcon
                      icon={AmpcoreMaterialIconId.CHEVRON_LEFT}
                      ariaLabel="Slide Left"
                    />
                  </button>
                  <button
                    type="button"
                    className="Carousel-arrow Carousel-arrowNext u-posAbsolute u-marginRight32"
                    aria-label="Slide Right"
                    onClick={moveRight}
                    disabled={!loop && position === slideCount - 1}
                    data-testid={testId ? `${testId}-slide-right` : null}
                    {...atb.dataAnalyticAttribute(
                      rightArrowDataAnalytics,
                      'slide-right'
                    )}
                  >
                    <MaterialIcon
                      icon={AmpcoreMaterialIconId.CHEVRON_RIGHT}
                      ariaLabel="Slide Right"
                    />
                  </button>
                </>
              )}

              <div
                className="u-hiddenVisually"
                aria-live="polite"
                data-testid={testId ? `${testId}-live-region` : null}
              >
                {`You are currently on slide ${position + 1} of ${slideCount}`}
              </div>
            </div>
          </div>
        </section>
      </div>
    </Spacer>
  )
}

const RATIO_SIZE_CLASSES: Record<
  NonNullable<CarouselSlide['ratio']>,
  { content: string; image: string }
> = {
  '5050': { content: 'u-size1of2', image: 'u-size1of2' },
  '7525': { content: 'u-size3of4', image: 'u-size1of4' },
  '2575': { content: 'u-size1of4', image: 'u-size3of4' }
}

/**
 * A slide with an image renders as two columns; without one, content spans the
 * full width. Inactive slides are pulled from the tab order so keyboard users
 * only reach the visible slide.
 */
function CarouselSlideContent({
  slide,
  isActive,
  variant = CarouselVariant.COMPLEX
}: {
  slide: CarouselSlide
  isActive: boolean
  variant?: CarouselVariant
}): JSX.Element {
  const tabbableProps = isActive ? {} : { tabIndex: -1 }

  const body = slide.content ? (
    <div
      className="Content content-block"
      dangerouslySetInnerHTML={{ __html: rteContentFilter(slide.content) }}
    />
  ) : null

  // Legacy layout: the heading + body sit above a full-width image as a single
  // column, rather than the two-column layout the complex variant uses.
  if (variant === CarouselVariant.LEGACY) {
    return (
      <div className="Carousel-slideLegacy u-sizeFull" {...tabbableProps}>
        <div className="Content Content--reversed u-marginBottom24">
          {slide.title && (
            <h3 className="u-marginTop20 u-marginBottom8 u-textColorWhite">
              {slide.title}
            </h3>
          )}
          {body}
        </div>
        {slide.imageSrc && (
          <div className="Content">
            <img
              className="u-sizeFull"
              src={slide.imageSrc}
              alt={slide.imageAlt ?? ''}
            />
          </div>
        )}
      </div>
    )
  }

  if (!slide.imageSrc) {
    // Content-only slides carry their own layout and horizontal spacing (e.g.
    // pre-rendered compound markup), so the wrapper adds none of its own.
    return (
      <div className="u-sizeFull" {...tabbableProps}>
        {slide.title && <h3>{slide.title}</h3>}
        {body}
      </div>
    )
  }

  const ratio = slide.ratio ?? '5050'
  const imagePosition = slide.imagePosition ?? 'right'
  const sizes = RATIO_SIZE_CLASSES[ratio]

  // The "near the gutter" padding (paddingRight on the left column, paddingLeft
  // on the right column) and the mobile bottom margin follow document order, so
  // they swap with the image when `imagePosition` is `left`.
  const leadingColumnPadding =
    'u-sm-paddingRight8 u-paddingRight20 u-sm-marginBottom16'
  const trailingColumnPadding = 'u-sm-paddingLeft8 u-paddingLeft20'

  const contentColumn = (
    <div
      key="content"
      className={`Carousel-contentColumn Content Grid u-sm-sizeFull u-sm-paddingVert8 u-paddingVert20 ${sizes.content} ${
        imagePosition === 'right' ? leadingColumnPadding : trailingColumnPadding
      }`}
      {...tabbableProps}
    >
      {slide.title && <h3>{slide.title}</h3>}
      {body}
    </div>
  )

  const imageColumn = (
    <div
      key="image"
      className={`Carousel-imageColumn Content Grid u-sm-sizeFull u-sm-paddingVert8 u-paddingVert20 ${sizes.image} ${
        imagePosition === 'right'
          ? trailingColumnPadding
          : `${leadingColumnPadding}`
      }`}
    >
      <figure className="Carousel-imageBlock">
        <img
          className="u-sizeFull"
          src={slide.imageSrc}
          alt={slide.imageAlt ?? ''}
        />
      </figure>
    </div>
  )

  return (
    <div className="TwoColumn Grid Grid--equalHeight u-sm-flexCol u-sm-paddingHoriz8 u-paddingHoriz80 u-flexAlignItemsCenter">
      {imagePosition === 'right'
        ? [contentColumn, imageColumn]
        : [imageColumn, contentColumn]}
    </div>
  )
}
